import {
	ApplicationFinancingTypeEnum,
	ApplicationStateEnum,
	OrderStateEnum,
} from '.spec/self-care/tsTypes';
import cx from 'classnames';
import { filter, map } from 'lodash/fp';
import React from 'react';

import { Amount } from '@skippay/core/@types/amount';
import { Application, Installment } from '@skippay/core/@types/payInThree';
import {
	TransactionBoxItem,
	TransactionBoxItemState,
} from '@skippay/core/@types/transactionBoxItem';
import iconDefault from '@skippay/core/assets/icons/transaction/icon-default.svg';
import iconPayInThree from '@skippay/core/assets/icons/transaction/icon-pay-in-three.svg';
import { ReactComponent as IconCrossCircle } from '@skippay/core/assets/icons/transaction/icon-remove.svg';
import { ReactComponent as IconReturned } from '@skippay/core/assets/icons/transaction/icon-returned.svg';
import { ReactComponent as IconWarning } from '@skippay/core/assets/icons/transaction/icon-warning.svg';
import FormattedCurrency from '@skippay/core/components/FormattedCurrency';
import FormattedDate from '@skippay/core/components/FormattedDate';
import Text from '@skippay/core/components/Text';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { sumAmounts, toFloat } from '@skippay/core/utils/amountUtils';
import { QueryFilter } from '@skippay/core/utils/apiUtils';

import { PAY_IN_THREE_DETAIL } from 'constants/routes';

export const CANCELLED_OR_PROCESSING = 'cancelledOrProcessing';
export const ONCOMING = 'oncoming';
export const OVERDUE = 'overdue';
export const PAID = 'paid';
export const PROCESSING = 'processing';

export type InstallmentState =
	| typeof CANCELLED_OR_PROCESSING
	| typeof ONCOMING
	| typeof OVERDUE
	| typeof PAID;

export const getRemainingInstallments = (installments: Installment[]): Installment[] => {
	return filter((installment) => installment.balance.amount > 0, installments);
};

export const getRemainingAmountToPay = (application: Application): Amount => {
	if (application.state === ApplicationStateEnum.PROCESSING) {
		return application.order.totalPrice;
	}
	const amounts = map(
		'balance',
		getRemainingInstallments(application.financing?.payInThree?.installments || [])
	);
	return sumAmounts(amounts);
};

export const getInstallmentState = (installment: Installment | undefined): InstallmentState => {
	if (installment?.claimWithoutSanctions.amount === 0) {
		return CANCELLED_OR_PROCESSING;
	}
	if (installment?.paymentDate) {
		return PAID;
	}
	if (installment?.currentDpd && installment?.currentDpd > 0) {
		return OVERDUE;
	}
	return ONCOMING;
};

export const hasOverdueInstallments = (application: Application): boolean => {
	return (
		(application.state === ApplicationStateEnum.READY &&
			application.financing?.payInThree?.installments.some(
				(installment) =>
					!installment.paymentDate && installment.currentDpd && installment.currentDpd > 0
			)) ||
		false
	);
};

export const nearestUnpaidInstallment = (
	installments: readonly Installment[] = []
): Installment | undefined =>
	installments
		.filter((installment) => !installment.paymentDate)
		.sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))[0];

export const getPayInThreeQueryFilter = (): QueryFilter =>
	new QueryFilter().prop('type').eq(ApplicationFinancingTypeEnum.PAY_IN_THREE);

const getState = (
	application: Application,
	nextPayment: Installment | undefined
): TransactionBoxItemState => {
	if (application.state === ApplicationStateEnum.PROCESSING) {
		return {
			color: 'colorWarning',
			text: (
				<FormattedMessage
					id="payInThreeApplication.list.processing.title"
					defaultMessage="Nedokončená objednávka"
				/>
			),
		};
	}
	if (application.state === ApplicationStateEnum.REJECTED) {
		return {
			color: 'colorError',
			icon: <IconWarning />,
			text: (
				<FormattedMessage
					id="payInThreeApplication.list.rejected.title"
					defaultMessage="Zamítnuto"
				/>
			),
		};
	}
	if (application.order.state === OrderStateEnum.RETURNED) {
		return {
			color: 'colorSuccess',
			icon: <IconReturned />,
			text: (
				<FormattedMessage id="payInThreeApplication.list.returned.title" defaultMessage="Vráceno" />
			),
		};
	}
	if (application.state === ApplicationStateEnum.CANCELLED) {
		return {
			color: 'colorWarning',
			icon: <IconCrossCircle />,
			text: (
				<FormattedMessage
					id="payInThreeApplication.list.cancelled.title"
					defaultMessage="Zrušeno"
				/>
			),
		};
	}
	if (application.state === ApplicationStateEnum.READY && application.isClosed) {
		return {
			color: 'colorSuccess',
			text: (
				<FormattedMessage
					defaultMessage="Doplaceno dne {paymentDate}"
					id="payInThreeApplication.list.paid.title"
					values={{ paymentDate: <FormattedDate value={application.financing.paymentDate} /> }}
				/>
			),
		};
	}
	if (
		application.state === ApplicationStateEnum.READY &&
		nextPayment?.currentDpd &&
		nextPayment?.currentDpd > 0
	) {
		return {
			color: 'colorError',
			text: (
				<FormattedMessage
					defaultMessage="Po splatnosti: {date}"
					id="payInThreeApplication.list.nextPayment.overdue.title"
					values={{
						date: <FormattedDate value={nextPayment.dueDate} />,
					}}
				/>
			),
		};
	}
	return {
		color: 'colorExtraLight',
		text: (
			<FormattedMessage
				defaultMessage="Příští platba: {date}"
				id="payInThreeApplication.list.nextPayment.title"
				values={{
					date: <FormattedDate value={nextPayment?.dueDate} />,
				}}
			/>
		),
	};
};

export const payInThreeToTransactionBoxItem = (
	data: {
		application: Application;
	},
	receiptItem?: boolean
): TransactionBoxItem => {
	const { application } = data;

	const receiptName = application.additionalInfoReceipt?.name;
	const nextPayment = application.financing.payInThree.installments.find(
		(installment) => !installment.paymentDate
	);
	const canApplyForInstallments = application.canApplyForInstallments ?? false;
	const { additionalInfoInstallments } = application;

	return {
		date: application.createdDate,
		id: application.id,
		price: (
			<Text as="strong" className="print-colorBase">
				<FormattedCurrency {...application.order.totalPrice} />
			</Text>
		),
		name: {
			title: receiptItem
				? `${receiptName} - ${application.eshopAdditionalInfo.name}`
				: application.eshopAdditionalInfo.name,
		},
		logo: receiptItem ? iconPayInThree : application.eshopAdditionalInfo.logoUrl || iconDefault,
		stateProps: receiptItem ? undefined : getState(application, nextPayment),
		to: {
			pathname: PAY_IN_THREE_DETAIL,
			params: { applicationId: application.id },
		},
		hasReceipt: !!receiptName,
		isPayInThree: true,
		isApplication: true,
		isApplicationInProcess: application.state === ApplicationStateEnum.PROCESSING,
		nextPaymentValue:
			nextPayment && toFloat(nextPayment.balance) > 0 ? (
				<Text
					as="strong"
					className={cx(
						'text-right text-xs',
						nextPayment.currentDpd && nextPayment.currentDpd > 0
							? 'text-red-500'
							: 'text-text-additional'
					)}
					size="paragraphSmall"
				>
					<FormattedCurrency {...nextPayment.balance} />
				</Text>
			) : undefined,
		canApplyForInstallments,
		additionalInfoInstallments,
	};
};

export const payInThreeToTransactionToReceipts = (
	applications: Application[]
): TransactionBoxItem[] => {
	return applications
		?.map((application) => payInThreeToTransactionBoxItem({ application }, true))
		.filter(Boolean);
};
